import {isConversationResolutionRequired} from '@github-ui/mergebox/helpers/conversation-resolution'
import type {PullRequestRuleFailureReason} from '@github-ui/mergebox/types'
import type {MergeabilityData} from '../use-mergeability-data'
import {selectFailedRequiredChecks} from '../use-failed-checks-data'

/**
 * The mutually-exclusive states for the ReviewSubmitted For You item. Priority order
 * matches the rubric so the higher-priority state always wins.
 *
 *  - `required-changes-requested`: a required reviewer (or required team member) has
 *    submitted a changes-requested review. Merge-blocking.
 *  - `comments-resolution-required`: the repo requires resolved threads and at least
 *    one unresolved review thread exists. Merge-blocking.
 *  - `approval-required`: fewer accepted approvals than required, a required reviewer
 *    is not approved, or a required team quota is unmet. Merge-blocking.
 *  - `address-comments`: there is a changes-requested review or unresolved threads,
 *    but the merge is not blocked by them. Merge-permitting advisory.
 *  - `none`: nothing to surface.
 */
export type ReviewSubmittedItemDecision =
  | {kind: 'none'}
  | {kind: 'required-changes-requested'}
  | {kind: 'comments-resolution-required'}
  | {kind: 'approval-required'}
  | {kind: 'address-comments'}

export type ReviewSubmittedItemKind = ReviewSubmittedItemDecision['kind']

const APPROVAL_REQUIRED_FAILURE_REASONS: PullRequestRuleFailureReason[] = [
  'MORE_REVIEWS_REQUIRED',
  'CODE_OWNER_REVIEW_REQUIRED',
  'SOC2_APPROVAL_PROCESS_REQUIRED',
  'LAST_PUSH_APPROVAL_REQUIRED',
]

/**
 * Single source of truth deciding which ReviewSubmitted variant should render.
 * Both the component and any orchestrating logic rely on this so the variants
 * remain mutually exclusive.
 *
 * Currently suppressed here (all outrank every ReviewSubmitted variant):
 *  - Row 1-4 (merged/merged-deleted/closed/draft): via `isClosedOrMerged` / `isDraft`.
 *  - Row 7 (conflicts): the current PR has merge conflicts.
 *  - Row 10 (required-failing-checks): the current PR has at least one failing required check.
 *
 * Selectively supressed:
 *  - Row 16 (required-to-update): the current PR is behind its base branch and the
 *    strict-required-status-checks policy blocks the merge until it's updated. The UpdateBranch
 *    item handles this state after higher-priority review blockers.
 *
 * Not yet suppressed - TODO: come back and modify the logic here once we have stack integration
 *  - Row 5 (stack-closed), 6 (stack-same-conflicts), 8 (stack-conflicts),
 *    9 (stack-same-required-checks-failing), 11 (stack-required-checks-failing),
 *    12 (stack-same-required-changes-requested), 14 (stack-required-changes-requested),
 *    17 (stack-same-approval-required), 19 (stack-approval-required),
 *    20 (stack-same-comments-to-address): all require `stackSection` on `MergeabilityData`.
 */
export function decideReviewSubmittedItem(
  data: MergeabilityData | null,
  hasUnresolvedThreads: boolean,
): ReviewSubmittedItemDecision {
  if (!data || data.isClosedOrMerged || data.mergeBoxPullRequest.isDraft) {
    return {kind: 'none'}
  }

  if (hasCurrentPullRequestConflicts(data) || hasFailingRequiredCheck(data)) {
    return {kind: 'none'}
  }

  const {reviewerSection, mergeRequirements, mergeSection} = data
  const failureReasons = reviewerSection?.consolidatedFailureReasons ?? []

  if (failureReasons.includes('CHANGES_REQUESTED')) {
    return {kind: 'required-changes-requested'}
  }

  if (isConversationResolutionRequired(mergeRequirements) && hasUnresolvedThreads) {
    return {kind: 'comments-resolution-required'}
  }

  if (mergeSection.requiresBranchUpdate) {
    return {kind: 'none'}
  }

  if (failureReasons.some(reason => APPROVAL_REQUIRED_FAILURE_REASONS.includes(reason))) {
    return {kind: 'approval-required'}
  }

  const hasChangesRequestedReview = (reviewerSection?.latestOpinionatedReviews ?? []).some(
    review => review.state === 'CHANGES_REQUESTED',
  )
  if (hasChangesRequestedReview || hasUnresolvedThreads) {
    return {kind: 'address-comments'}
  }

  return {kind: 'none'}
}

// Row 7: the current PR itself has unresolved merge conflicts.
function hasCurrentPullRequestConflicts(data: MergeabilityData): boolean {
  const status = data.conflictsSection?.sectionStatus
  return status === 'HAS_CONFLICTS' || status === 'HAS_REBASE_CONFLICTS'
}

// Row 10: the current PR has at least one failing required check.
function hasFailingRequiredCheck(data: MergeabilityData): boolean {
  return selectFailedRequiredChecks(data.statusChecks).length > 0
}
